import React, { useState, useEffect } from 'react';
import { Button, TextField, Container, Typography, Box, Link, InputAdornment, IconButton, Paper, Avatar } from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Visibility, VisibilityOff, LockOutlined } from '@mui/icons-material';
import { login, getCurrentUser } from '../services/authService';
import { getFirstAccessibleRoute } from '../utils/navigationHelper';
import { useAuth } from '../contexts/AuthContext';
import Loader from './Loader';
import { toast } from 'react-toastify';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [justLoggedIn, setJustLoggedIn] = useState(false); // ✅ Track fresh login
  const navigate = useNavigate();
  const location = useLocation();
  const { modulePermissions, loading: authLoading } = useAuth(); // ✅ Get permissions and loading state

  // ✅ Handle navigation for existing logged-in user
  useEffect(() => {
    const checkUser = async () => {
      const user = getCurrentUser();
      if (user && !justLoggedIn) {
        // ✅ Wait for permissions to load before navigating
        if (authLoading) {
          return;
        }
        
        try {
          // ✅ Pass permissions from AuthContext to avoid duplicate API call
          const firstRoute = await getFirstAccessibleRoute(user.id || user.email, user.role, modulePermissions);
          navigate(firstRoute, { replace: true });
        } catch (error) {
          console.error('❌ Error finding accessible route:', error);
          navigate('/dashboard', { replace: true });
        }
      }
    };
    
    checkUser();
  }, [navigate, modulePermissions, authLoading, justLoggedIn]); // ✅ Include authLoading to trigger when loading completes

  // ✅ Handle navigation after fresh login
  useEffect(() => {
    const handlePostLogin = async () => {
      if (justLoggedIn && !authLoading) {
        const user = getCurrentUser();
        if (user) {
          try {
            let targetRoute = location.state?.from?.pathname;
            
            if (!targetRoute || targetRoute === '/login') {
              targetRoute = await getFirstAccessibleRoute(user.id || user.email, user.role, modulePermissions);
            }
            
            navigate(targetRoute, { replace: true });
          } catch (error) {
            console.error('❌ Error finding accessible route:', error);
            navigate('/dashboard', { replace: true });
          }
        }
      }
    };
    
    handlePostLogin();
  }, [justLoggedIn, authLoading, modulePermissions, navigate, location]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    setLoading(true);

    try {
      
      // ✅ Get both user and company from login response
      const { user, company } = await login(email, password);
      
      
      // ✅ Set flag to trigger post-login navigation after permissions load
      setJustLoggedIn(true);
      
    } catch (error) {
      console.error('❌ Login failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        overflow: 'hidden',
        position: 'fixed',
        top: 0,
        left: 0,
      }}
    >
      <Container maxWidth="xs" sx={{ height: 'auto' }}>
        <Paper
          elevation={10}
          sx={{
            p: 4,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="/assets/logo.png"
              alt="Logo"
              sx={{
                width: 80,
                height: 80,
                objectFit: 'contain',
                borderRadius: '16px',
                backgroundColor: '#fff',
                padding: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                mb: 2,
              }}
            />

            {/* Lock Icon Avatar */}
            <Avatar
              sx={{
                m: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                width: 56,
                height: 56,
              }}
            >
              <LockOutlined sx={{ fontSize: 32 }} />
            </Avatar>

            {/* Title */}
            <Typography
              component="h1"
              variant="h4"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Welcome Back
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              Sign in to continue to Billeur
            </Typography>

            {/* Form */}
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        disabled={loading}
                        sx={{
                          color: '#667eea',
                          '&:hover': {
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          },
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  sx={{
                    fontSize: '0.875rem',
                    color: '#667eea',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                    transition: 'all 0.2s',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #a0a0a0 0%, #808080 100%)',
                  },
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            © 2025 Billeur. All rights reserved.
          </Typography>
        </Box>
      </Container>
      <Loader open={loading} />
    </Box>
  );
};

export default Login;